import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout';
import { useI18n } from '../../contexts/I18nContext';

export default function Cookies() {
  const { legal, isRTL } = useI18n();
  const cookies = legal.legal.privacy.cookies;
  const sections = cookies.sections;

  const getSectionText = (section: { content?: string; description?: string }) => {
    if ('content' in section && typeof section.content === 'string') {
      return section.content;
    }
    if ('description' in section && typeof section.description === 'string') {
      return section.description;
    }
    return '';
  };

  const whatAreCookiesNote =
    'note' in sections.whatAreCookies && typeof sections.whatAreCookies.note === 'string'
      ? sections.whatAreCookies.note
      : '';

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F8F5FF] to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-4xl">
          {/* Back Link */}
          <div className="pt-16 mb-8">
            <Link
              to="/legal"
              className={`inline-flex items-center text-[#5B3FD0] hover:text-[#4A2FB0] transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <svg className={`w-5 h-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {legal.legal.pages.cookies.title}
            </Link>
          </div>

          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border-t-4 border-[#5B3FD0]">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4">{cookies.title}</h1>
            <p className="text-[#707070] mb-2">{cookies.lastUpdated}</p>
            <p className="text-[#707070] mb-2">{cookies.effectiveDate}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#707070] mt-4">
              <span>{cookies.appInfo.appName.label} <strong>{cookies.appInfo.appName.value}</strong></span>
              <span>{cookies.appInfo.developer.label} <strong>{cookies.appInfo.developer.value}</strong></span>
              <span>{cookies.appInfo.contact.label} <strong>{cookies.appInfo.contact.value}</strong></span>
              <span>{cookies.appInfo.website.label} <a href={cookies.appInfo.website.value} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">{cookies.appInfo.website.value}</a></span>
            </div>
          </div>

          {/* Introduction */}
          <Section title={sections.introduction.title}>
            {Array.isArray(sections.introduction.content) ? (
              sections.introduction.content.map((p, i) => (
                <p key={i} className="text-[#555] leading-relaxed mb-3">{p}</p>
              ))
            ) : (
              <p className="text-[#555] leading-relaxed mb-3">{sections.introduction.content}</p>
            )}
          </Section>

          {/* What Are Cookies */}
          <Section title={sections.whatAreCookies.title}>
            {Array.isArray(sections.whatAreCookies.content) ? (
              sections.whatAreCookies.content.map((p, i) => (
                <p key={i} className="text-[#555] leading-relaxed mb-3">{p}</p>
              ))
            ) : (
              <p className="text-[#555] leading-relaxed mb-3">{sections.whatAreCookies.content}</p>
            )}
            {whatAreCookiesNote && (
              <p className="text-sm text-[#707070] italic">{whatAreCookiesNote}</p>
            )}
          </Section>

          {/* Why We Use Cookies */}
          <Section title={sections.whyWeUseCookies.title}>
            <p className="text-[#555] mb-3">{sections.whyWeUseCookies.description}</p>
            <ul className="space-y-2 list-disc list-inside text-[#555]">
              {sections.whyWeUseCookies.purposes.map((item, i) => (
                <li key={i} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          </Section>

          {/* Types of Cookies */}
          <Section title={sections.types.title}>
            {/* Essential */}
            <CookieTypeBlock
              title={sections.types.essential.title}
              description={sections.types.essential.description}
              examples={sections.types.essential.examples}
              note={sections.types.essential.note}
              color="red"
            />

            {/* Functional */}
            <CookieTypeBlock
              title={sections.types.functional.title}
              description={sections.types.functional.description}
              examples={sections.types.functional.examples}
              note={sections.types.functional.note}
              color="blue"
            />

            {/* Analytics */}
            <CookieTypeBlock
              title={sections.types.analytics.title}
              description={sections.types.analytics.description}
              examples={sections.types.analytics.examples}
              note={sections.types.analytics.note}
              color="yellow"
            />

            {/* Marketing */}
            <CookieTypeBlock
              title={sections.types.marketing.title}
              description={sections.types.marketing.description}
              examples={sections.types.marketing.examples}
              note={sections.types.marketing.note}
              color="green"
            />
          </Section>

          {/* First-Party and Third-Party */}
          <Section title={sections.firstAndThirdParty.title}>
            <SubSection title={sections.firstAndThirdParty.firstParty.title}>
              <p className="text-[#555] leading-relaxed">{sections.firstAndThirdParty.firstParty.content}</p>
            </SubSection>

            <SubSection title={sections.firstAndThirdParty.thirdParty.title}>
              <p className="text-[#555] mb-3">{sections.firstAndThirdParty.thirdParty.description}</p>
              <ul className="space-y-3">
                {sections.firstAndThirdParty.thirdParty.services.map((item, i) => (
                  <li key={i} className="text-[#555] leading-relaxed">
                    <strong className="text-[#2C2C2C]">{item.name}:</strong> {item.description}
                    {'link' in item && item.link && (
                      <>
                        {' '}
                        <a href={item.link} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">
                          {item.linkText || item.link}
                        </a>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#707070] mt-3 italic">{sections.firstAndThirdParty.thirdParty.note}</p>
            </SubSection>
          </Section>

          {/* Session vs Persistent */}
          <Section title={sections.sessionVsPersistent.title}>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-[#F8F5FF] rounded-lg p-6">
                <h4 className="font-bold text-[#2C2C2C] mb-2">{sections.sessionVsPersistent.session.title}</h4>
                <p className="text-[#555] leading-relaxed">{getSectionText(sections.sessionVsPersistent.session)}</p>
              </div>
              <div className="bg-[#F8F5FF] rounded-lg p-6">
                <h4 className="font-bold text-[#2C2C2C] mb-2">{sections.sessionVsPersistent.persistent.title}</h4>
                <p className="text-[#555] leading-relaxed">{getSectionText(sections.sessionVsPersistent.persistent)}</p>
              </div>
            </div>
          </Section>

          {/* Managing Cookies */}
          <Section title={sections.managing.title}>
            {/* Browser Settings */}
            <SubSection title={sections.managing.browserSettings.title}>
              <p className="text-[#555] mb-3">{sections.managing.browserSettings.description}</p>
              <ul className="space-y-2 list-disc list-inside text-[#555] mb-4">
                {sections.managing.browserSettings.options.map((item, i) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-3">
                {sections.managing.browserSettings.browserLinks.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#F8F5FF] text-[#5B3FD0] rounded-lg hover:bg-[#EDE8FF] transition-colors text-sm font-medium"
                  >
                    {link.browser}
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ))}
              </div>
            </SubSection>

            {/* Mobile App Settings */}
            <SubSection title={sections.managing.mobileAppSettings.title}>
              <p className="text-[#555] mb-3">{sections.managing.mobileAppSettings.description}</p>
              <ul className="space-y-2">
                {sections.managing.mobileAppSettings.options.map((item, i) => (
                  <li key={i} className="text-[#555] leading-relaxed">
                    <strong className="text-[#2C2C2C]">{item.name}:</strong> {item.description}
                  </li>
                ))}
              </ul>
            </SubSection>

            {/* Disabling Impact */}
            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-bold text-amber-800 mb-1">{sections.managing.impactOfDisabling.title}</h4>
              <p className="text-amber-700">{sections.managing.impactOfDisabling.note}</p>
            </div>
          </Section>

          {/* Do Not Track */}
          <Section title={sections.doNotTrack.title}>
            <p className="text-[#555] leading-relaxed">{getSectionText(sections.doNotTrack)}</p>
          </Section>

          {/* Updates */}
          <Section title={sections.updates.title}>
            <p className="text-[#555] leading-relaxed">{getSectionText(sections.updates)}</p>
          </Section>

          {/* Contact */}
          <Section title={sections.contact.title}>
            <p className="text-[#555] mb-4">{sections.contact.description}</p>
          </Section>

          {/* Cookie Summary Table */}
          <Section title={sections.cookieSummary.title}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#5B3FD0] text-white">
                    <th className="px-4 py-3 text-left font-semibold">{sections.cookieSummary.tableHeaders.cookieType}</th>
                    <th className="px-4 py-3 text-left font-semibold">{sections.cookieSummary.tableHeaders.purpose}</th>
                    <th className="px-4 py-3 text-left font-semibold">{sections.cookieSummary.tableHeaders.canDisable}</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.cookieSummary.rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F5FF]'}>
                      <td className="px-4 py-3 text-[#2C2C2C] font-medium border-b">{row.cookieType}</td>
                      <td className="px-4 py-3 text-[#555] border-b">{row.purpose}</td>
                      <td className="px-4 py-3 border-b">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${row.canDisable === 'No' || row.canDisable === 'لا' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                          {row.canDisable}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-sm text-[#707070]">
              © {new Date().getFullYear()} {legal.legal.mainPage.footer.rights} <strong>{legal.legal.mainPage.company}</strong>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
      <h2 className="text-2xl font-bold text-[#2C2C2C] mb-4 pb-2 border-b-2 border-[#5B3FD0]">{title}</h2>
      {children}
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-lg font-semibold text-[#2C2C2C] mb-3">{title}</h3>
      {children}
    </div>
  );
}

function CookieTypeBlock({
  title,
  description,
  examples,
  note,
  color,
}: {
  title: string;
  description: string;
  examples: { name: string; description: string }[];
  note: string;
  color: string;
}) {
  const colorMap: Record<string, string> = {
    red: 'border-l-red-500',
    blue: 'border-l-blue-500',
    yellow: 'border-l-amber-500',
    green: 'border-l-green-500',
  };

  return (
    <div className={`mt-6 border-l-4 ${colorMap[color] || 'border-l-[#5B3FD0]'} bg-[#FAFAFA] rounded-r-lg p-6`}>
      <h4 className="text-lg font-bold text-[#2C2C2C] mb-2">{title}</h4>
      <p className="text-[#555] mb-3">{description}</p>
      <ul className="space-y-2">
        {examples.map((ex, i) => (
          <li key={i} className="text-[#555]">
            <strong className="text-[#2C2C2C]">{ex.name}:</strong> {ex.description}
          </li>
        ))}
      </ul>
      <p className="text-sm text-[#707070] mt-3 italic">{note}</p>
    </div>
  );
}
